import { animate, style, transition, trigger } from '@angular/animations'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { OverlayModule } from '@angular/cdk/overlay'
import {
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'

import { AngularReactModule } from '@bubblydoo/angular-react'
import { Store } from '@ngrx/store'
import { OAuthService } from 'angular-oauth2-oidc'
import { TooltipModule } from 'primeng/tooltip'

import { filter, map, startWith } from 'rxjs'

import { FlxButtonComponent, FlxCardComponent } from '@flowx/angular-ui-toolkit'
import { getActiveUiFlowSessionId, urlUpdate$ } from '@flowx/core-sdk'
import { CommentModeStore } from '@flowx/designer/modules/ai-builder/store/comment-mode.store'
import { getTenantId } from '@flowx/designer/modules/auth/tenant-id.state'
import { RunDesignerLinkStore } from '@flowx/designer/modules/layout/store/run-designer-link.store'
import { PanelSize } from '@flowx/designer/modules/process-details/process-details-helper.service'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import { FlxReusableTemplateInfoComponent } from '@flowx/designer/modules/reusable-ui-template/components/reusable-template-info/reusable-template-info.component'
import {
  ApplicationStatus,
  ApplicationType,
} from '@flowx/designer/shared/entities/application.entity'
import { UiFlowSession } from '@flowx/designer/shared/entities/ui-flow-sessions.types'
import { UiFlowSessionsService } from '@flowx/designer/shared/services/ui-flow-sessions.service'
import {
  selectQueryParam,
  selectRouteParam,
} from '@flowx/designer/shared/store/selectors/router.selectors'
import { FlxUiFlowRendererPortal } from '@flowx/react-sdk'

import { UiFlowSessionDetailsPanelComponent } from '../../components/ui-flow-session-details-panel/ui-flow-session-details-panel.component'

@Component({
  selector: 'flx-ui-flow-running-instance',
  imports: [
    RouterLink,
    FlxButtonComponent,
    FlxCardComponent,
    FlxReusableTemplateInfoComponent,
    TooltipModule,
    OverlayModule,
    UiFlowSessionDetailsPanelComponent,
    AngularReactModule,
  ],
  animations: [
    trigger('fadeIn', [
      transition('void => *', [
        style({ transform: 'translateY(100vh)' }),
        animate(300, style({ transform: 'translateY(0)' })),
      ]),
      transition('* => void', [
        style({ transform: 'translateY(0)' }),
        animate(300, style({ transform: 'translateY(100vh)' })),
      ]),
    ]),
  ],
  templateUrl: './ui-flow-running-instance.component.html',
  styleUrl: './ui-flow-running-instance.component.scss',
  encapsulation: ViewEncapsulation.None,
  host: {
    class: `block pt-20 pr-20 pl-24 h-full overflow-auto flex flex-col h-full flx-ui-flow-renderer`,
  },
})
export class UiFlowRunningInstanceComponent implements OnInit, OnDestroy {
  #store = inject(Store)
  #uiFlowSessionsService = inject(UiFlowSessionsService)
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #route = inject(ActivatedRoute)
  #destroyRef = inject(DestroyRef)
  #authService = inject(OAuthService)
  #commentMode = inject(CommentModeStore)
  #runDesignerLink = inject(RunDesignerLinkStore)

  #mutationObserver: MutationObserver | null = null
  #withdrawCommentMode: (() => void) | null = null
  #baseReactProps: Record<string, unknown>
  #processUuid = toSignal(
    this.#route.queryParams.pipe(map((params) => params.processInstanceUuid ?? params.processUuid))
  ) // selectQueryParam does not emit correctly, so we use toSignal
  #instanceDataOpenedQueryParam = toSignal(
    this.#route.queryParams.pipe(map((params) => params.instanceDataOpened)) // selectQueryParam does not emit correctly, so we use toSignal
  )

  router = inject(Router)
  apiUrl = inject('BASE_URL' as any)
  staticAssetsPath = inject('STATIC_ASSETS_PATH' as any)
  processApiPath = inject('PROCESS_API_PATH' as any)

  uiFlowResourceId = input<string>()

  authToken = signal(localStorage.getItem('access_token'))
  startParams = (window.history.state?.startParams ?? {}) as Record<string, unknown>
  workspaceId = this.#store.selectSignal(selectRouteParam('workspaceId'))
  projectId = this.#store.selectSignal(selectRouteParam('appId'))
  buildId = this.#store.selectSignal(selectRouteParam('appVersionId'))
  uiFlowName = this.#store.selectSignal(selectQueryParam('uiFlowName'))
  themeId = this.#store.selectSignal(selectQueryParam('themeId'))
  language = this.#store.selectSignal(selectQueryParam('language'))
  locale = this.#store.selectSignal(selectQueryParam('locale'))

  showUiFlowInfo = signal<boolean>(false)

  reusableInfo = computed(() => ({
    appName: this.#applicationDetailsStore.appName(),
    appVersionId: this.#applicationDetailsStore.appVersionId(),
    branchName: this.#applicationDetailsStore.branchName(),
    status: this.#applicationDetailsStore.isVersionCommitted() ? 'COMMITTED' : 'WIP',
    type: this.#applicationDetailsStore.appType() === ApplicationType.APP ? 'Project' : 'Library',
  }))

  designerLink = computed(() => [
    '/',
    'guard',
    'workspace',
    this.workspaceId(),
    'projects',
    this.projectId(),
    'config',
    this.buildId(),
    'ui-flows',
    this.uiFlowResourceId(),
    'ui',
  ])
  uiFlowsLink = computed(() => [
    '/',
    'guard',
    'workspace',
    this.workspaceId(),
    'projects',
    this.projectId(),
    'config',
    this.buildId(),
    'ui-flows',
  ])

  uiFlowSessionUuid = toSignal(
    urlUpdate$.pipe(
      map((url: URL) => {
        const params: Record<string, string> = {}
        url.searchParams.forEach((value, key) => {
          params[key] = value
        })
        return params['uiFlowSessionUuid']
      }),
      // Seed from the route so the uuid is available even when the SDK never
      // republishes it on urlUpdate$ — which is the case for blocked sessions
      // (access-denied / expired), where handleStartUiFlowResponse short-circuits
      // before emitting. Without this seed, "View UI Flow instance" is a no-op on
      // those screens because showInstanceData() gates on uiFlowSessionUuid().
      startWith(this.#route.snapshot.queryParamMap.get('uiFlowSessionUuid') ?? undefined),
      filter((uiFlowSessionUuid) => !!uiFlowSessionUuid),
      takeUntilDestroyed(this.#destroyRef)
    )
  )
  uiFlowSession = signal<UiFlowSession>(null)
  uiFlowProcessInstances = signal<any[]>([])
  instanceDataOpened = computed(() => coerceBooleanProperty(this.#instanceDataOpenedQueryParam()))

  readonly FlxUiFlowRenderer = FlxUiFlowRendererPortal

  organizationId = getTenantId()

  // Base props are snapshotted once in ngOnInit: query params are dropped on
  // nav-away before this component is destroyed, so deriving them reactively
  // would re-render the React renderer with undefined props and trigger a
  // spurious re-init (e.g. theme fetch with uuid `undefined`). Those values
  // never change while the page is alive.
  // accessToken is the one exception: it's sourced from OAuth events (not query
  // params), so it's safe to keep live — the renderer's init effects deliberately
  // exclude it from their deps, so a token-only change never re-inits the flow.
  reactProps = computed(() => ({
    ...this.#baseReactProps,
    accessToken: this.authToken(),
    commentMode: this.#commentMode.on(),
  }))

  constructor() {
    // Comment mode is for a run that started from the head of its branch: the work in progress, or
    // the latest commit. The store keeps the previous version until this run's one arrives, so a
    // version that is not the run's counts as not at the head.
    effect(() => {
      const version = this.#applicationDetailsStore.applicationVersion()
      const usedByRun = !!version && version.id === this.buildId()

      this.#commentMode.reportRunVersion({
        atBranchHead:
          usedByRun && (version.status === ApplicationStatus.WIP || version.lastVersionInBranch),
        branchName: usedByRun ? version.branchName : '',
      })
    })

    effect(() => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const processUuid = this.#processUuid() as string
      const uiFlowSessionUuid = this.uiFlowSessionUuid() as string
      const panelOpen = this.instanceDataOpened()

      if (!uiFlowSessionUuid || !panelOpen) {
        return
      }

      this.#uiFlowSessionsService
        .getUiFlowSession(uiFlowSessionUuid)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe((response) => {
          this.uiFlowSession.set(response.session)
          this.uiFlowProcessInstances.set(response.processInstances)
        })
    })
  }

  ngOnInit(): void {
    this.#baseReactProps = {
      enginePath: this.processApiPath,
      baseUrl: this.apiUrl,
      assetsBaseUrl: this.staticAssetsPath,
      language: this.language(),
      themeId: this.themeId(),
      uiFlowName: this.uiFlowName(),
      appInfo: {
        appId: this.projectId(),
        appVersionId: this.buildId(),
        selectedProcessResourceId: this.uiFlowResourceId(),
      },
      workspaceId: this.workspaceId(),
      organizationId: this.organizationId,
      locale: this.locale(),
      cache: true,
      isDraft: true,
      startParams: this.startParams,
    }

    this.#authService.events
      .pipe(
        filter((event) => event.type === 'token_received'),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        this.authToken.set(this.#authService.getAccessToken())
      })

    this.#applicationDetailsStore.getApplicationVersion(this.projectId(), this.buildId())

    this.#withdrawCommentMode = this.#commentMode.offerFromPage(() => ({
      resourceType: 'ui-flow',
      resourceId: this.uiFlowResourceId(),
      runId: this.uiFlowSessionUuid() ?? getActiveUiFlowSessionId() ?? '',
      appVersionId: this.buildId(),
      resourceName: [this.uiFlowName()].flat()[0] || 'UI flow',
    }))
    this.#runDesignerLink.remember({ appId: this.projectId(), commands: this.designerLink() })

    // SDK writes these to window.history directly; mirror into Router so 'merge' navs preserve them.
    urlUpdate$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((url) => {
      this.router.navigate([], {
        relativeTo: this.#route,
        queryParams: {
          uiFlowSessionUuid: url.searchParams.get('uiFlowSessionUuid'),
          modalId: url.searchParams.get('modalId'),
          viewId: url.searchParams.get('viewId'),
          chatSessionId: url.searchParams.get('chatSessionId'),
        },
        queryParamsHandling: 'merge',
        skipLocationChange: true,
      })
    })
  }

  ngOnDestroy(): void {
    this.#mutationObserver?.disconnect()
    // The link is left behind on purpose: this page is gone by the time Config is picked on the
    // runtime page the maker goes to, and Config is what follows it.
    this.#withdrawCommentMode?.()
  }

  showInstanceData(): void {
    if (this.uiFlowSessionUuid()) {
      this.router.navigate([], {
        relativeTo: this.#route,
        queryParams: {
          instanceDataOpened: true,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
    }
  }

  hideInstanceData(): void {
    this.router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        instanceDataOpened: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  onSizeChange(size: PanelSize): void {
    if (size === PanelSize.SMALL) {
      this.hideInstanceData()
    }
  }
}
